import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation files
import en from './locales/en.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import it from './locales/it.json';

// Translation resources
const resources = {
  en: { translation: en },
  de: { translation: de },
  fr: { translation: fr },
  it: { translation: it },
} as const;

// Supported languages
export const SUPPORTED_LANGUAGES = [
  { code: 'de', name: 'Deutsch', flag: '🇨🇭' },
  { code: 'fr', name: 'Français', flag: '🇨🇭' },
  { code: 'it', name: 'Italiano', flag: '🇨🇭' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
] as const;

export type SupportedLanguage = 'en' | 'de' | 'fr' | 'it';

// Language detection order
const detectionOrder = [
  'localStorage',
  'navigator',
  'htmlTag',
  'path',
  'subdomain',
];

// Configure i18next
i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: import.meta.env.DEV,

    // Language detection configuration
    detection: {
      order: detectionOrder,
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
      lookupFromPathIndex: 0,
      lookupFromSubdomainIndex: 0,
    },

    // Interpolation configuration
    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // React configuration
    react: {
      useSuspense: true,
    },

    // Return null for missing keys in production
    returnNull: !import.meta.env.DEV,

    // Namespace configuration (for future expansion)
    defaultNS: 'translation',
    ns: ['translation'],

    // Pluralization
    pluralSeparator: '_',
    contextSeparator: '_',
  });

// Helper to get current language
export const getCurrentLanguage = (): SupportedLanguage => {
  const lang = i18n.language || 'en';
  // Handle variants like 'en-US' -> 'en'
  const baseLanguage = lang.split('-')[0] as SupportedLanguage;
  return ['en', 'de', 'fr', 'it'].includes(baseLanguage) ? baseLanguage : 'en';
};

// Helper to change language
export const changeLanguage = async (lang: SupportedLanguage): Promise<void> => {
  await i18n.changeLanguage(lang);
  // Store in localStorage
  localStorage.setItem('i18nextLng', lang);
  // Update HTML lang attribute
  document.documentElement.lang = lang;
};

// Initialize HTML lang attribute on load
document.documentElement.lang = getCurrentLanguage();

export default i18n;
